import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import { usePageTitle } from '@/hooks/usePageTitle';
import FaturaBelgesi from '@/components/FaturaBelgesi';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Building2, Calendar, Package, Receipt, Warehouse, AlertTriangle, CheckCircle, Clock, Pencil } from 'lucide-react';

const fmtTR = (n, d = 2) =>
  n == null ? '—' : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });

const statusMap = {
  paid: { label: 'Ödendi', cls: 'bg-[#3d6e4e]/10 text-[#3d6e4e]', icon: CheckCircle },
  pending: { label: 'Bekliyor', cls: 'bg-[#7a6a2e]/10 text-[#7a6a2e]', icon: Clock },
  overdue: { label: 'Gecikti', cls: 'bg-[#7a3535]/10 text-[#7a3535]', icon: AlertTriangle },
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/invoices');
  const { role } = useAuth();
  const canEdit = role === 'super_admin' || role === 'admin';
  const [invoice, setInvoice] = useState(null);
  usePageTitle(invoice ? 'Fatura ' + invoice.invoice_number : 'Fatura Detayı');
  const [supplier, setSupplier] = useState(null);
  const [warehouse, setWarehouse] = useState(null);

  useEffect(() => {
    api.get(`/invoices/${id}`).then(r => {
      setInvoice(r.data);
      if (r.data.supplier_id) {
        api.get(`/suppliers/${r.data.supplier_id}`).then(sr => setSupplier(sr.data)).catch(() => {});
      }
      if (r.data.warehouse_id) {
        api.get(`/warehouses/${r.data.warehouse_id}`).then(wr => setWarehouse(wr.data)).catch(() => {});
      }
    }).catch(() => navigate('/erp/invoices'));
  }, [id]);

  if (!invoice) return <div className="text-center py-8 text-[#8a7e70]">Yükleniyor...</div>;

  const status = statusMap[invoice.status] || statusMap.pending;
  const StatusIcon = status.icon;
  const matchedItems = (invoice.items || []).filter(i => i.matched_product_id);
  const unmatchedItems = (invoice.items || []).filter(i => !i.matched_product_id);

  return (
    <div data-testid="invoice-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]">
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-[#1a1916] tracking-tight">
              Fatura {invoice.invoice_number || '—'}
            </h1>
            <span className={`inline-flex items-center gap-1 status-badge ${status.cls}`}>
              <StatusIcon size={11} />
              {status.label}
            </span>
          </div>
          {/* Başlıkta GERÇEK cari durur. Eskiden burada `parsed_supplier_name`
              vardı ve fatura M.Kemal Deniz'e bağlıyken başlıkta "ŞAH MARKET LTD"
              yazıyordu.

              `parsed_supplier_name` AI'nın okuma DENEMESİDİR, belgede yazan
              değil: kullanıcı cariyi elle düzeltince bu alan güncellenmiyor,
              yanlış okuma kayıtta kalıyor. Bu yüzden "belgede şu yazıyor" gibi
              bir iddiada KULLANILAMAZ — öyle bir uyarı denendi, taranan 12
              uyuşmazlığın hepsi AI hatası çıktı, cari seçimleri doğruydu. */}
          <p className="text-sm text-[#8a7e70] mt-0.5">
            {supplier?.name || invoice.parsed_supplier_name || '—'} — {invoice.invoice_date}
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => navigate(`/erp/invoices/${id}/edit`)}
            className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold">
            <Pencil size={15} className="mr-2" /> Düzenle
          </Button>
        )}
      </div>

      {/* Özet kartlar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-[#8a7e70] uppercase mb-1">Tutar</p>
            <p className="text-xl font-extrabold text-[#1a1916]">{fmtTR(invoice.amount_foreign)} <span className="text-sm font-normal text-[#8a7e70]">{invoice.currency}</span></p>
            {invoice.currency !== 'TRY' && <p className="text-xs text-[#8a7e70]">{fmtTR(invoice.amount_try)} TRY</p>}
          </CardContent>
        </Card>
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-[#8a7e70] uppercase mb-1">Fatura Tarihi</p>
            <p className="text-sm font-semibold text-[#1a1916]">{invoice.invoice_date || '—'}</p>
          </CardContent>
        </Card>
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-[#8a7e70] uppercase mb-1">Vade Tarihi</p>
            <p className={`text-sm font-semibold ${invoice.status === 'overdue' ? 'text-[#7a3535]' : 'text-[#1a1916]'}`}>
              {invoice.due_date || '—'}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-[#8a7e70] uppercase mb-1">Kalem Sayısı</p>
            <p className="text-xl font-extrabold text-[#1a1916]">{(invoice.items || []).length}</p>
            {unmatchedItems.length > 0 && (
              <p className="text-xs text-[#7a6a2e]">{unmatchedItems.length} eşleşmedi</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Tedarikçi */}
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardHeader className="pb-2 border-b border-[#e8dfd0]">
            <div className="flex items-center gap-2">
              <Building2 size={14} className="text-[#8a7e70]" />
              <CardTitle className="text-sm font-bold text-[#1a1916]">Tedarikçi</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-1">
            {supplier ? (
              <Link to={`/erp/suppliers/${supplier.id}`} className="text-sm font-semibold text-[#1a1916] hover:underline block">
                {supplier.name}
              </Link>
            ) : (
              <p className="text-sm font-semibold text-[#1a1916]">{invoice.parsed_supplier_name || '—'}</p>
            )}
            {invoice.parsed_supplier_tax && <p className="text-xs text-[#8a7e70]">VN: {invoice.parsed_supplier_tax}</p>}
          </CardContent>
        </Card>

        {/* Depo */}
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardHeader className="pb-2 border-b border-[#e8dfd0]">
            <div className="flex items-center gap-2">
              <Warehouse size={14} className="text-[#8a7e70]" />
              <CardTitle className="text-sm font-bold text-[#1a1916]">Giriş Deposu</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-[#1a1916]">{warehouse?.name || '—'}</p>
          </CardContent>
        </Card>

        {/* Notlar */}
        {invoice.notes && (
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="pb-2 border-b border-[#e8dfd0]">
              <CardTitle className="text-sm font-bold text-[#1a1916]">Notlar</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <p className="text-sm text-[#3F3E39]">{invoice.notes}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Fatura kalemleri */}
      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        <CardHeader className="border-b border-[#e8dfd0] flex flex-row items-center gap-2 pb-3">
          <Package size={15} className="text-[#8a7e70]" />
          <CardTitle className="text-base font-bold text-[#1a1916]">
            Fatura Kalemleri
            <span className="ml-2 text-xs font-normal text-[#8a7e70]">({(invoice.items || []).length} kalem)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Ürün</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Ürün Kodu</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Miktar</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Birim</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Birim Fiyat</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Toplam</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">KDV %</TableHead>
                <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Eşleşme</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(invoice.items || []).map((item, i) => (
                <TableRow key={i} className={`erp-table-row border-[#d0c4b0] ${!item.matched_product_id ? 'bg-[#7a6a2e]/3' : ''}`}>
                  <TableCell className="font-medium text-[#1a1916]">
                    {item.matched_product_name || item.description}
                    {item.matched_product_name && item.description && item.matched_product_name !== item.description && (
                      <span className="block text-xs text-[#8a7e70] font-normal">{item.description}</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-[#8a7e70]">{item.matched_product_code || item.product_code || item.bilnex_code || '—'}</TableCell>
                  {/* Ambalaj içeriği olan satırlarda ham miktar/fiyat yanıltıcı:
                      "25 KG @ 107,14" yazıyor ama stoğa 25 × 0,5 = 12,5 kg
                      girmiş ve birim fiyat 214,28 TL/kg olmuş. Sistemin
                      gerçekte kaydettiğini de gösteriyoruz. */}
                  {(() => {
                    const icerik = parseFloat(item.package_qty) || 0;
                    const ambalajli = icerik > 0 && Math.abs(icerik - 1) > 1e-9;
                    const anaBirim = item.package_unit || item.unit || '';
                    return (
                      <>
                        <TableCell className="text-right font-mono text-sm">
                          {fmtTR(item.quantity, 3)}
                          {ambalajli && (
                            <span className="block text-[11px] font-sans text-[#3d6e4e]"
                              title="Stoğa giren miktar">
                              × {fmtTR(icerik, 3)} = {fmtTR((parseFloat(item.quantity) || 0) * icerik, 3)} {anaBirim}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-[#3F3E39]">{item.unit}</TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {fmtTR(item.unit_price)}
                          {ambalajli && (
                            <span className="block text-[11px] font-sans text-[#3d6e4e]"
                              title="Ana birim başına fiyat">
                              ↳ {fmtTR((parseFloat(item.unit_price) || 0) / icerik)} /{anaBirim}
                            </span>
                          )}
                        </TableCell>
                      </>
                    );
                  })()}
                  <TableCell className="text-right font-mono font-semibold text-[#1a1916]">{fmtTR(item.total_price)}</TableCell>
                  <TableCell className="text-right text-sm text-[#8a7e70]">%{item.kdv_rate ?? item.vat_rate ?? 0}</TableCell>
                  <TableCell>
                    {item.matched_product_id ? (
                      <span className="status-badge bg-[#3d6e4e]/10 text-[#3d6e4e] text-[10px]">Eşleşti</span>
                    ) : (
                      <span className="status-badge bg-[#7a6a2e]/10 text-[#7a6a2e] text-[10px]">Gider</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!(invoice.items || []).length && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-[#8a7e70]">Kalem bulunamadı</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Fatura görseli — dosya kutusundaki TÜM sayfalar.
          `image_data` tek görsel tutuyor; çok sayfalı belgede yalnızca biri
          görünüyordu. Sayfalar GridFS'te ve belge faturaya bağlı, oradan
          listeleniyor. Kutudan geçmemiş eski faturalarda `image_data` yedek. */}
      <FaturaBelgesi invoiceId={id} imageData={invoice.image_data} />
    </div>
  );
}
